"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { v4 as uuidv4 } from 'uuid';

// Address types
const ADDRESS_TYPES = ['Home', 'Office', 'Other'];

export default function AddressesPage() {
  const { user, addAddress, updateAddress, deleteAddress, setDefaultAddress } = useUser();
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });
  
  // For Google Maps
  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const markerRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    type: 'Home',
    label: '',
    fullAddress: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Pakistan',
    lat: 0,
    lng: 0,
    isDefault: false,
  });
  
  // Check if Google Maps is loaded
  useEffect(() => {
    // Function to check if Google Maps is loaded
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately if Google Maps is already loaded
    if (checkGoogleMapsLoaded()) return;

    // If not loaded, set up an interval to check periodically
    const intervalId = setInterval(() => {
      if (checkGoogleMapsLoaded()) {
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, []);
  
  useEffect(() => {
    setAddresses(user?.addresses || []);
  }, [user?.addresses]);
  
  // Initialize Google Map when modal is opened
  useEffect(() => {
    if (isModalOpen && googleMapsLoaded) {
      // Default to center of Pakistan if no coordinates are provided
      const defaultCenter = { lat: 30.3753, lng: 69.3451 };
      const center = currentAddress ? 
        { lat: currentAddress.lat || defaultCenter.lat, lng: currentAddress.lng || defaultCenter.lng } : 
        defaultCenter;
      
      const mapOptions = {
        center,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false
      };
      
      const map = new window.google.maps.Map(mapRef.current, mapOptions);
      
      // Create a search box
      const searchBox = new window.google.maps.places.SearchBox(searchBoxRef.current);
      map.controls[window.google.maps.ControlPosition.TOP_CENTER].push(searchBoxRef.current);
      
      // Create a marker
      const marker = new window.google.maps.Marker({
        position: center,
        map,
        draggable: true,
        animation: window.google.maps.Animation.DROP
      });
      
      markerRef.current = marker;
      
      // Bias the search box results towards the current map viewport
      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds());
      });
      
      // Listen for the event fired when the user selects a prediction and retrieve more details
      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        
        if (places.length === 0) return;
        
        const place = places[0];
        
        // For each place, get the icon, name and location.
        if (!place.geometry || !place.geometry.location) return;
        
        // If the place has a geometry, then present it on a map.
        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(17);
        }
        
        marker.setPosition(place.geometry.location);
        
        // Extract address components
        let street = '';
        let city = '';
        let state = '';
        let postalCode = '';
        let country = '';
        
        for (const component of place.address_components) {
          const componentType = component.types[0];
          
          switch (componentType) {
            case 'street_number':
              street = `${component.long_name} `;
              break;
            case 'route':
              street += component.long_name;
              break;
            case 'locality':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'postal_code':
              postalCode = component.long_name;
              break;
            case 'country':
              country = component.long_name;
              break;
          }
        }
        
        // Update form data with selected place information
        setFormData({
          ...formData,
          fullAddress: place.formatted_address,
          street,
          city,
          state,
          postalCode,
          country,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
      });
      
      // Update form data when marker is dragged
      marker.addListener('dragend', () => {
        const position = marker.getPosition();
        const geocoder = new window.google.maps.Geocoder();
        
        geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK' && results[0]) {
            // Extract address components as above
            let street = '';
            let city = '';
            let state = '';
            let postalCode = '';
            let country = '';
            
            for (const component of results[0].address_components) {
              const componentType = component.types[0];
              
              switch (componentType) {
                case 'street_number':
                  street = `${component.long_name} `;
                  break;
                case 'route':
                  street += component.long_name;
                  break;
                case 'locality':
                  city = component.long_name;
                  break;
                case 'administrative_area_level_1':
                  state = component.long_name;
                  break;
                case 'postal_code':
                  postalCode = component.long_name;
                  break;
                case 'country':
                  country = component.long_name;
                  break;
              }
            }
            
            // Update form data with new address information
            setFormData({
              ...formData,
              fullAddress: results[0].formatted_address,
              street,
              city,
              state,
              postalCode,
              country,
              lat: position.lat(),
              lng: position.lng()
            });
          }
        });
      });
    }
  }, [isModalOpen, googleMapsLoaded, currentAddress, formData]);
  
  const openModal = (address = null) => {
    if (address) {
      setCurrentAddress(address);
      setFormData({
        id: address.id,
        type: address.type || 'Home',
        label: address.label || '',
        fullAddress: address.fullAddress || '',
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        postalCode: address.postalCode || '',
        country: address.country || 'Pakistan',
        lat: address.lat || 0,
        lng: address.lng || 0,
        isDefault: address.isDefault || false,
      });
    } else {
      setCurrentAddress(null);
      setFormData({
        id: uuidv4(),
        type: 'Home',
        label: '',
        fullAddress: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Pakistan',
        lat: 0,
        lng: 0,
        isDefault: addresses.length === 0, // Make default if first address
      });
    }
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentAddress(null);
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    try {
      // Validation
      if (!formData.fullAddress) {
        setNotification({
          type: 'error',
          message: 'Please select an address from the map'
        });
        return;
      }
      
      if (currentAddress) {
        // Update existing address
        updateAddress(formData.id, formData);
      } else {
        // Add new address
        addAddress(formData);
      }
      
      // If this address is set as default
      if (formData.isDefault) {
        setDefaultAddress(formData.id);
      }
      
      setNotification({
        type: 'success',
        message: currentAddress ? 'Address updated successfully!' : 'Address added successfully!'
      });
      
      // Clear notification after 3 seconds
      setTimeout(() => {
        setNotification({ type: '', message: '' });
      }, 3000);
      
      closeModal();
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to save address. Please try again.'
      });
    }
  };
  
  const handleDeleteAddress = (id) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      deleteAddress(id);
      
      setNotification({
        type: 'success',
        message: 'Address deleted successfully!'
      });
      
      // Clear notification after 3 seconds
      setTimeout(() => {
        setNotification({ type: '', message: '' });
      }, 3000);
    }
  };
  
  const handleSetAsDefault = (id) => {
    setDefaultAddress(id);
    
    setNotification({
      type: 'success',
      message: 'Default address updated successfully!'
    });
    
    // Clear notification after 3 seconds
    setTimeout(() => {
      setNotification({ type: '', message: '' });
    }, 3000);
  };
  
  // Update document title and meta tags manually
  useEffect(() => {
    // Set document title
    document.title = "My Addresses | Delivery Locations | OpenMenu.pk";
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Manage your delivery addresses for faster checkout on OpenMenu.pk. Add, edit, or remove delivery locations.";
  }, []);
  
  return (
    <>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Your Addresses</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Add and manage your delivery addresses</p>
          </div>
          <button
            onClick={() => openModal()}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Add Address
          </button>
        </div>
        
        {notification.message && (
          <div className={`px-4 py-3 ${notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {notification.message}
          </div>
        )}
        
        <div className="border-t border-gray-200">
          {addresses.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No addresses</h3>
              <p className="mt-1 text-sm text-gray-500">Add a new address to get started.</p>
              <div className="mt-6">
                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Address
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {addresses.map(address => (
                <li key={address.id} className="px-4 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          {address.type}
                        </span>
                        {address.isDefault && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-900 font-medium">{address.label || address.type}</p>
                      <p className="mt-1 text-sm text-gray-500">{address.fullAddress}</p>
                    </div>
                    <div className="flex-shrink-0 ml-4 flex">
                      <button
                        onClick={() => openModal(address)}
                        className="mr-2 inline-flex items-center px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Edit
                      </button>
                      {!address.isDefault && (
                        <button
                          onClick={() => handleSetAsDefault(address.id)}
                          className="mr-2 inline-flex items-center px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteAddress(address.id)}
                        className="inline-flex items-center px-3 py-1 border border-red-300 rounded text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      
      {/* Address Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {currentAddress ? 'Edit Address' : 'Add New Address'}
                    </h3>
                    
                    <form onSubmit={handleSubmit} className="mt-4">
                      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-2">
                          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                            Address Type
                          </label>
                          <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                          >
                            {ADDRESS_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="sm:col-span-4">
                          <label htmlFor="label" className="block text-sm font-medium text-gray-700">
                            Label (Optional)
                          </label>
                          <input
                            type="text"
                            name="label"
                            id="label"
                            placeholder="e.g. My Apartment, Parents' House"
                            value={formData.label}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                          />
                        </div>
                        
                        <div className="sm:col-span-6">
                          <label htmlFor="map-search" className="block text-sm font-medium text-gray-700 mb-1">
                            Search for address or drag the marker
                          </label>
                          <input
                            ref={searchBoxRef}
                            id="map-search"
                            type="text"
                            placeholder="Search for location..."
                            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                          />
                        </div>
                        
                        <div className="sm:col-span-6">
                          <div ref={mapRef} className="w-full h-64 rounded-md border border-gray-300"></div>
                        </div>
                        
                        <div className="sm:col-span-6">
                          <label htmlFor="fullAddress" className="block text-sm font-medium text-gray-700">
                            Full Address
                          </label>
                          <textarea
                            id="fullAddress"
                            name="fullAddress"
                            rows={2}
                            value={formData.fullAddress}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                            placeholder="Full address will appear here after selecting from map"
                            readOnly
                          />
                        </div>
                        
                        <div className="sm:col-span-6">
                          <div className="flex items-center">
                            <input
                              id="isDefault"
                              name="isDefault"
                              type="checkbox"
                              checked={formData.isDefault}
                              onChange={handleChange}
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
                              Set as default address
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          {currentAddress ? 'Update Address' : 'Save Address'}
                        </button>
                        <button
                          type="button"
                          onClick={closeModal}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 